import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, storage } from './firebase';

const COLLECTIONS = {
  SEEKER_PROFILES: 'seekerProfiles',
  FRANCHISES: 'franchises'
};

/**
 * Find franchise covering a specific location
 * @param {string} taluka - Taluka name
 * @param {string} district - District name
 * @param {string} state - State name
 * @returns {Promise<string|null>} Franchise ID or null
 */
const findFranchiseForLocation = async (taluka, district, state) => {
  try {
    // Query active franchises
    const q = query(
      collection(db, COLLECTIONS.FRANCHISES),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);

    // Iterate to find matching area
    // We do client-side filtering because assignedAreas is an array of objects
    for (const doc of snapshot.docs) {
      const franchise = doc.data();
      if (franchise.assignedAreas && Array.isArray(franchise.assignedAreas)) {
        const hasAccess = franchise.assignedAreas.some(area =>
          area.state === state &&
          area.district === district &&
          area.taluka === taluka
        );

        if (hasAccess) {
          console.log(`Found franchise ${doc.id} for location: ${taluka}, ${district}`);
          return doc.id;
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Error searching for franchise:', error);
    return null;
  }
};

/**
 * Create or update seeker profile
 * @param {string} userId - User ID
 * @param {Object} profileData - Profile data
 * @returns {Object} Result object
 */
export const createOrUpdateSeekerProfile = async (userId, profileData) => {
  try {
    console.log('=== SAVING PROFILE TO DATABASE ===');
    console.log('User ID:', userId);
    console.log('Profile Data:', profileData);
    console.log('Aadhar Card URL in profile data:', profileData.aadharCardURL);
    console.log('Aadhar Card FileName in profile data:', profileData.aadharCardFileName);
    console.log('Date of Birth in profile data:', profileData.personalInfo?.dateOfBirth);

    const profileRef = doc(db, COLLECTIONS.SEEKER_PROFILES, userId);

    // Check for Franchise Assignment based on location
    let franchiseIdToSave = profileData.franchiseId; // Preserve existing if present in update

    if (profileData.personalInfo?.taluka && profileData.personalInfo?.district && profileData.personalInfo?.state) {
      const { taluka, district, state } = profileData.personalInfo;
      const foundFranchiseId = await findFranchiseForLocation(taluka, district, state);

      if (foundFranchiseId) {
        franchiseIdToSave = foundFranchiseId;
        console.log('Assigning user to franchise:', franchiseIdToSave);
      }
    }

    const profileWithTimestamp = {
      ...profileData,
      userId,
      updatedAt: Timestamp.now(),
      createdAt: profileData.createdAt || Timestamp.now()
    };

    if (franchiseIdToSave) {
      profileWithTimestamp.franchiseId = franchiseIdToSave;

      // Also update the user document
      try {
        const userRef = doc(db, 'users', userId);
        // Using setDoc with merge: true to avoid errors if doc doesn't exist (though it should)
        // User requested to save franchiseId under 'referredBy' field in users collection
        await setDoc(userRef, { referredBy: franchiseIdToSave }, { merge: true });
        console.log('Updated user document with referredBy:', franchiseIdToSave);
      } catch (err) {
        console.error('Error updating user document with referredBy:', err);
      }
    }

    console.log('Final data to be saved:', profileWithTimestamp);
    console.log('Aadhar Card URL in final data:', profileWithTimestamp.aadharCardURL);
    console.log('Aadhar Card FileName in final data:', profileWithTimestamp.aadharCardFileName);

    await setDoc(profileRef, profileWithTimestamp, { merge: true });

    console.log('=== DATA SAVED SUCCESSFULLY ===');
    console.log('Saved profile data:', profileWithTimestamp);

    return {
      success: true,
      message: 'Profile updated successfully',
      profile: profileWithTimestamp
    };
  } catch (error) {
    console.error('Error creating/updating seeker profile:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get seeker profile by user ID
 * @param {string} userId - User ID
 * @returns {Object} Result object with profile data
 */
export const getSeekerProfile = async (userId) => {
  try {
    const profileRef = doc(db, COLLECTIONS.SEEKER_PROFILES, userId);
    const profileDoc = await getDoc(profileRef);

    if (profileDoc.exists()) {
      const profileData = profileDoc.data();
      return {
        success: true,
        profile: {
          id: profileDoc.id,
          ...profileData,
          createdAt: profileData.createdAt?.toDate(),
          updatedAt: profileData.updatedAt?.toDate()
        }
      };
    } else {
      return {
        success: false,
        error: 'Profile not found',
        profile: null
      };
    }
  } catch (error) {
    console.error('Error fetching seeker profile:', error);
    return {
      success: false,
      error: error.message,
      profile: null
    };
  }
};

/**
 * Upload file to Firebase Storage (Resume or Aadhar Card)
 * @param {string} userId - User ID
 * @param {File} file - File to upload
 * @param {string} type - Type of file ('resume' or 'aadhar')
 * @returns {Object} Result object with download URL
 */
export const uploadResume = async (userId, file, type = 'resume') => {
  try {
    if (!file) {
      return { success: false, error: 'No file provided' };
    }

    // Validate file type based on upload type
    let allowedTypes;
    let folderName;
    let filePrefix;

    if (type === 'aadhar') {
      allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      folderName = 'aadharCards';
      filePrefix = 'aadhar';
    } else {
      allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      folderName = 'resumes';
      filePrefix = 'resume';
    }

    if (!allowedTypes.includes(file.type)) {
      const typeNames = type === 'aadhar'
        ? 'PDF, JPG, or PNG'
        : 'PDF, DOC, or DOCX';
      return {
        success: false,
        error: `Invalid file type. Please upload ${typeNames} files only.`
      };
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'File size too large. Please upload files smaller than 5MB.'
      };
    }

    // Create file reference
    const timestamp = Date.now();
    const fileName = `${userId}_${filePrefix}_${timestamp}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `${folderName}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      success: true,
      downloadURL,
      fileName,
      message: `${type === 'aadhar' ? 'Aadhar card' : 'Resume'} uploaded successfully`
    };
  } catch (error) {
    console.error(`Error uploading ${type}:`, error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete resume from Firebase Storage
 * @param {string} resumeURL - Resume download URL
 * @returns {Object} Result object
 */
export const deleteResume = async (resumeURL) => {
  try {
    if (!resumeURL) {
      return { success: true, message: 'No resume to delete' };
    }

    const storageRef = ref(storage, resumeURL);
    await deleteObject(storageRef);

    return {
      success: true,
      message: 'Resume deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting resume:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if seeker profile is complete
 * @param {Object} profile - Profile object
 * @returns {Object} Completion status and missing fields
 */
export const checkProfileCompletion = (profile) => {
  const requiredFields = [
    'personalInfo.firstName',
    'personalInfo.lastName',
    'personalInfo.email',
    'personalInfo.phone',
    'personalInfo.state',
    'personalInfo.district',
    'personalInfo.taluka',
    'personalInfo.pincode',
    'professionalSummary',
    'experience',
    'education',
    'skills',
    'preferences'
  ];

  const missingFields = [];
  let completionPercentage = 0;

  requiredFields.forEach(field => {
    const fieldValue = getNestedValue(profile, field);
    // for preferences we check if it has entries, but preferences is an object, so we need deeper check or just check if it exists
    // Actually, let's refine the check for preferences essentially in the loop or customized logic below if needed.
    // simpler to just expect it to be truthy for now, but better to check specific subfields? 
    // The user wants ALL fields required. So let's rely on validation in the UI for specifics, but here we just check if preferences object exists and is not empty.
    // However, getNestedValue for 'preferences' will return the object.
    // let's stick to simple existence for now, or maybe check keys.
    if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || (typeof fieldValue === 'object' && Object.keys(fieldValue).length === 0)) {
      missingFields.push(field);
    } else {
      completionPercentage += (100 / requiredFields.length);
    }
  });

  // Bonus points for optional fields
  const optionalFields = ['resumeURL', 'aadharCardURL', 'portfolio', 'certifications', 'languages'];
  optionalFields.forEach(field => {
    const fieldValue = getNestedValue(profile, field);
    if (fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0)) {
      completionPercentage += 5; // 5% bonus for each optional field
    }
  });

  return {
    isComplete: missingFields.length === 0,
    completionPercentage: Math.min(Math.round(completionPercentage), 100),
    missingFields
  };
};

/**
 * Get nested object value by dot notation
 * @param {Object} obj - Object to search
 * @param {string} path - Dot notation path
 * @returns {*} Value at path
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Search seeker profiles (for employers)
 * @param {Object} filters - Search filters
 * @param {Object} options - Query options
 * @returns {Object} Result object with profiles
 */
export const searchSeekerProfiles = async (filters = {}, options = {}) => {
  try {
    let q = collection(db, COLLECTIONS.SEEKER_PROFILES);

    // Apply filters
    if (filters.skills && filters.skills.length > 0) {
      q = query(q, where('skills', 'array-contains-any', filters.skills));
    }

    if (filters.location) {
      q = query(q, where('personalInfo.location', '>=', filters.location));
      q = query(q, where('personalInfo.location', '<=', filters.location + '\uf8ff'));
    }

    if (filters.experienceLevel) {
      q = query(q, where('experienceLevel', '==', filters.experienceLevel));
    }

    const querySnapshot = await getDocs(q);
    const profiles = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    }));

    return {
      success: true,
      profiles: profiles.slice(0, options.limit || 50)
    };
  } catch (error) {
    console.error('Error searching seeker profiles:', error);
    return {
      success: false,
      error: error.message,
      profiles: []
    };
  }
};

/**
 * Get profile completion statistics
 * @param {string} userId - User ID
 * @returns {Object} Profile statistics
 */
export const getProfileStats = async (userId) => {
  try {
    const profileResult = await getSeekerProfile(userId);

    if (!profileResult.success) {
      return {
        success: false,
        error: profileResult.error
      };
    }

    const completion = checkProfileCompletion(profileResult.profile);

    return {
      success: true,
      stats: {
        ...completion,
        lastUpdated: profileResult.profile.updatedAt,
        hasResume: !!profileResult.profile.resumeURL,
        hasAadharCard: !!profileResult.profile.aadharCardURL
      }
    };
  } catch (error) {
    console.error('Error getting profile stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
